import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { Check, ChevronDown, Plus } from "lucide-react";
import { cn } from "../../../lib/utils.ts";
import {
  WEEK_NAME_MAX_LENGTH,
  resolveTypedWeek,
  sameWeek,
  withWeek,
} from "./weeks.ts";

// The design kit's picker: the same panel and rows as the Play List's
// GamePicker and the reports' MultiGamePicker. Floating (anchored), the
// height cap and gap go through Headless UI's anchor variables — its inline
// max-height would override max-h-80, and a margin overlaps when it flips up.
const PANEL =
  "z-50 flex w-56 flex-col gap-1 overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-lg focus:outline-none";
const PANEL_FLOATING = "[--anchor-gap:4px] [--anchor-max-height:20rem]";
const PANEL_INLINE = "absolute left-0 top-full mt-1 max-h-80";
const ROW =
  "flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none";

/**
 * Commits a week still being typed in the list, if any. The Save Game dialog
 * calls it just before saving: on the iPad, tapping Save closes the list in
 * the same tap, too late for that save to see the typed week.
 */
export type CommitPendingWeek = MutableRefObject<(() => void) | null>;

type Props = {
  value: string | null;
  /** The team's weeks (libraryWeekNames). */
  weeks: readonly string[];
  /** A week picked from the list or typed in; null = no week. */
  onChange: (week: string | null) => void;
  /** What the picker is for ("Week of Eagan vs Burnsville"). */
  label: string;
  /**
   * "field": a picker button (Save Game dialog); the list opens inside the
   * dialog, which keeps taps and focus from leaving it. "cell": the week
   * itself is the button (Game Library); the list floats over the table, so
   * the column never changes width.
   */
  variant: "field" | "cell";
  /** See CommitPendingWeek. */
  commitPendingRef?: CommitPendingWeek;
};

/**
 * The practice-week picker (client 2026-10-06): "No week", the team's weeks
 * and "+ New week" at the end, which becomes a box to type the new week's
 * name — inside the list, so nothing around it moves. A typed week is kept
 * when the list closes (Add, Enter, or a tap elsewhere — like renaming a
 * game); only Escape drops it.
 */
export function WeekPicker({
  value,
  weeks,
  onChange,
  label,
  variant,
  commitPendingRef,
}: Props) {
  const shown = value ?? (variant === "cell" ? "Add week" : "No week");
  // Set as the whole picker goes (its row left the table, the app went
  // offline, the dialog closed): a half-typed week is dropped, not saved.
  // React runs this cleanup before the list's own.
  const gone = useRef(false);
  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
    };
  }, []);
  return (
    <Popover className={variant === "field" ? "relative flex-1" : "relative"}>
      <PopoverButton
        // The week is part of the name, so screen readers hear it.
        aria-label={`${label}: ${shown}`}
        title={variant === "cell" && value ? value : undefined}
        className={
          variant === "field"
            ? "inline-flex h-10 w-full items-center justify-between gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
            : // Taller tap target than the text, without a taller row.
              "-my-1 block max-w-[10rem] truncate rounded-sm py-1 text-left underline decoration-gray-300 decoration-dotted underline-offset-4 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 data-[open]:text-blue-600"
        }
      >
        {variant === "field" ? (
          <>
            <span className={cn("truncate", !value && "font-normal text-gray-400")}>
              {shown}
            </span>
            <ChevronDown size={14} className="shrink-0 text-slate-400" />
          </>
        ) : (
          <span className={value ? undefined : "text-gray-400"}>{shown}</span>
        )}
      </PopoverButton>
      <PopoverPanel
        anchor={variant === "cell" ? "bottom start" : undefined}
        data-week-picker-panel=""
        className={cn(PANEL, variant === "cell" ? PANEL_FLOATING : PANEL_INLINE)}
      >
        {({ close }) => (
          <WeekOptions
            value={value}
            weeks={weeks}
            label={label}
            commitPendingRef={commitPendingRef}
            pickerGone={gone}
            onClose={close}
            onPick={(week) => {
              close();
              if (!sameWeek(week, value)) onChange(week);
            }}
            onKeepTyped={(week) => {
              if (!sameWeek(week, value)) onChange(week);
            }}
          />
        )}
      </PopoverPanel>
    </Popover>
  );
}

/** The list's rows. Mounted only while it is open, so typing starts fresh. */
function WeekOptions({
  value,
  weeks,
  label,
  commitPendingRef,
  pickerGone,
  onClose,
  onPick,
  onKeepTyped,
}: {
  value: string | null;
  weeks: readonly string[];
  label: string;
  commitPendingRef?: CommitPendingWeek;
  pickerGone: MutableRefObject<boolean>;
  onClose: () => void;
  /** A row, Add or Enter: take it and close. */
  onPick: (week: string | null) => void;
  /** A typed week kept as the list goes away (or just before a save). */
  onKeepTyped: (week: string) => void;
}) {
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState("");
  const typed = typing ? resolveTypedWeek(draft, weeks) : null;

  // Read when the list goes away, after the last render.
  const live = useRef({ typed, onClose, onKeepTyped });
  live.current = { typed, onClose, onKeepTyped };
  // Picked, added or Escaped: nothing left to keep.
  const settled = useRef(false);

  const pick = (week: string | null) => {
    settled.current = true;
    onPick(week);
  };
  const keepTyped = () => {
    const week = live.current.typed;
    if (settled.current || !week) return;
    settled.current = true;
    live.current.onKeepTyped(week);
  };

  useEffect(() => {
    // Escape closes just this list, dropping anything typed — caught before
    // anything else sees it. In the Save Game dialog, the dialog would
    // otherwise close too (taking the typed game name with it), and stopping
    // only the dialog leaves the list open: Headless UI skips an Escape that
    // something already handled. An Escape that ends IME composition is the
    // keyboard's (Safari flags it only with keyCode 229): it closes nothing —
    // not the list, and not the dialog either.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      if (event.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      settled.current = true;
      live.current.onClose();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  useEffect(() => {
    if (commitPendingRef) commitPendingRef.current = keepTyped;
    return () => {
      if (commitPendingRef?.current === keepTyped) commitPendingRef.current = null;
      // Closed by a tap elsewhere with a week typed: keep it. (Read now, on
      // purpose: the picker's own cleanup has just run if it is going too.)
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (!pickerGone.current) keepTyped();
    };
    // keepTyped reads everything through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const row = (week: string | null, text: string) => (
    <button
      key={week ?? ""}
      type="button"
      aria-pressed={sameWeek(value, week)}
      className={ROW}
      onClick={() => pick(week)}
    >
      {sameWeek(value, week) ? (
        <Check className="h-4 w-4 shrink-0 text-blue-600" />
      ) : (
        <span className="h-4 w-4 shrink-0" />
      )}
      <span className="truncate" title={week ?? undefined}>
        {text}
      </span>
    </button>
  );

  return (
    <>
      <div className="px-3 py-1 text-sm text-neutral-900">Weeks</div>
      {row(null, "No week")}
      {withWeek(weeks, value).map((week) => row(week, week))}
      {typing ? (
        <div className="flex items-center gap-1.5 px-2 py-1">
          <input
            autoFocus
            value={draft}
            maxLength={WEEK_NAME_MAX_LENGTH}
            placeholder="New week name"
            aria-label={`${label}: new week name`}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // A key that ends IME composition (Enter, Escape) is the
              // keyboard's: kept from the list, which would close on Escape.
              if (e.nativeEvent.isComposing || e.keyCode === 229) {
                e.stopPropagation();
                return;
              }
              if (e.key === "Enter") {
                e.preventDefault();
                if (typed) pick(typed);
              }
            }}
            className="h-8 min-w-0 flex-1 rounded-md border border-gray-300 px-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
          <button
            type="button"
            onClick={() => typed && pick(typed)}
            disabled={!typed}
            className="h-8 shrink-0 rounded-md bg-blue-600 px-3 text-sm font-medium text-white hover:bg-blue-500 disabled:pointer-events-none disabled:opacity-50"
          >
            Add
          </button>
        </div>
      ) : (
        <button type="button" className={ROW} onClick={() => setTyping(true)}>
          <Plus className="h-4 w-4 shrink-0 text-slate-500" />
          <span>New week</span>
        </button>
      )}
    </>
  );
}
